// views/Dashboard/PainelExecutivo/sections/ceo/DinheiroDoPeriodo.tsx
//
// Recebido, previsão e inadimplência — os três números de dinheiro que o Igor
// pediu — no período que ele escolher. Período de um mês abre no RESUMO; de
// vários meses abre no GRÁFICO, com a inadimplência mês a mês, e um botão troca
// entre os dois.
//
// Tudo sai do mesmo grupo de boletos (os que vencem no período) e de uma conta
// só (`calcularDinheiroDoPeriodo`): pago + atrasado + a vencer = o que foi
// cobrado. O "cobrado" é a moldura da barra, não um quarto número — na versão
// anterior ele aparecia ao lado da previsão como "Esperado" e as duas palavras
// pareciam dizer a mesma coisa.
import React, { useMemo, useState } from 'react';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ComposedChart, LabelList, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { LineChart as IconeGrafico, Rows3 } from 'lucide-react';
import { useFinanceiroDashboardData } from '../financeiro/useFinanceiroDashboardData';
import { calcularDinheiroDoPeriodo, serieDiariaDinheiro, serieMensalDinheiro } from '../../../../../src/lib/dashboard/painelCeo';
import type { DiaDinheiro, DinheiroDoPeriodo as Dinheiro, MesDinheiro } from '../../../../../src/lib/dashboard/painelCeo';
import { calcularJanelaPeriodoDashboard, yyyyMmParaMesReferencia } from '../../../../../src/lib/dashboard/periodoDashboard';
import type { PeriodoDashboard } from '../../../../../src/lib/dashboard/periodoDashboard';
import { ultimoDiaUtilDoMes } from '../../../../../src/lib/reguaPrazosAgrupamento';
import { Alternador, Bloco, ErroBloco, LinhasCarregando, Surgir, brl, brlCurto, pct, useModoEscuro, useMontado, CURVA_SAIDA } from './ui';

const isoLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const yyyyMm = (d: Date) => isoLocal(d).slice(0, 7);
const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const pctTexto = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

/**
 * Cores das marcas, validadas para daltonismo (scripts/validate_palette.js da
 * skill dataviz, 19/10/2026). O verde-oliva da marca com vermelho dava ΔE 5,5
 * para protanopia — recebido e atrasado viravam a mesma cor. Verde um pouco mais
 * vivo com laranja passa nos dois temas.
 */
const CORES = {
  claro: { recebido: '#55702A', atrasado: '#F2994A', textura: 'rgba(85,112,42,0.38)', fundo: 'rgba(85,112,42,0.10)', grade: '#eef0f2', eixo: '#9ca3af', superficie: '#ffffff' },
  escuro: { recebido: '#56772C', atrasado: '#CF7F33', textura: 'rgba(163,181,101,0.42)', fundo: 'rgba(163,181,101,0.10)', grade: '#334155', eixo: '#6b7280', superficie: '#1f2937' },
};
type Cores = typeof CORES.claro;

const TEXTURA_CSS = (c: Cores) => `repeating-linear-gradient(-45deg, ${c.textura} 0 3px, ${c.fundo} 3px 7px)`;

// ─────────────── seletor de período ───────────────

type Atalho = 'este' | 'passado' | '12' | 'mes' | 'periodo';

const ATALHOS: { id: Atalho; rotulo: string }[] = [
  { id: 'este', rotulo: 'Este mês' },
  { id: 'passado', rotulo: 'Mês passado' },
  { id: '12', rotulo: '12 meses' },
  { id: 'mes', rotulo: 'Escolher mês' },
  { id: 'periodo', rotulo: 'Período' },
];

function atalhoDe(p: PeriodoDashboard): Atalho {
  if (p.tipo === 'mesEspecifico') return 'mes';
  if (p.tipo === 'range') return 'periodo';
  if (p.preset === 'mesAnterior') return 'passado';
  if (p.preset === 'ultimos12') return '12';
  return 'este';
}

function periodoDoAtalho(a: Atalho, agora: Date): PeriodoDashboard {
  const passado = yyyyMm(new Date(agora.getFullYear(), agora.getMonth() - 1, 1));
  switch (a) {
    case 'passado': return { tipo: 'preset', preset: 'mesAnterior' };
    case '12': return { tipo: 'preset', preset: 'ultimos12' };
    case 'mes': return { tipo: 'mesEspecifico', mes: passado };
    case 'periodo': return { tipo: 'range', inicio: yyyyMm(new Date(agora.getFullYear(), agora.getMonth() - 5, 1)), fim: yyyyMm(agora) };
    default: return { tipo: 'preset', preset: 'mesAtual' };
  }
}

const CAMPO_MES =
  'rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold tabular-nums text-gray-800 outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';

const SeletorPeriodo: React.FC<{ periodo: PeriodoDashboard; agora: Date; onChange: (p: PeriodoDashboard) => void }> = ({ periodo, agora, onChange }) => {
  const ativo = atalhoDe(periodo);
  const limite = yyyyMm(new Date(agora.getFullYear(), agora.getMonth() + 12, 1));
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex flex-wrap items-center gap-0.5 rounded-full bg-gray-100 p-1 dark:bg-gray-900/60" role="group" aria-label="Período">
        {ATALHOS.map((a) => (
          <button
            key={a.id}
            type="button"
            aria-pressed={ativo === a.id}
            onClick={() => ativo !== a.id && onChange(periodoDoAtalho(a.id, agora))}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-[background-color,color,transform] duration-150 ease-out active:scale-[0.97] ${
              ativo === a.id
                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100'
            }`}
          >
            {a.rotulo}
          </button>
        ))}
      </div>
      {periodo.tipo === 'mesEspecifico' && (
        <input type="month" aria-label="Mês" max={limite} value={periodo.mes}
          onChange={(e) => e.target.value && onChange({ tipo: 'mesEspecifico', mes: e.target.value })} className={CAMPO_MES} />
      )}
      {periodo.tipo === 'range' && (
        // No celular os dois campos dividem a linha. `w-0` zera a largura própria do
        // input de mês (o nome do mês por extenso): sem isso os dois passavam da tela.
        <span className="flex w-full min-w-0 items-center gap-1.5 sm:w-auto">
          <input type="month" aria-label="Do mês" max={limite} value={periodo.inicio}
            onChange={(e) => e.target.value && onChange({ ...periodo, inicio: e.target.value })} className={`${CAMPO_MES} w-0 min-w-0 flex-1 sm:w-auto sm:flex-none`} />
          <span className="text-xs text-gray-400">até</span>
          <input type="month" aria-label="Até o mês" max={limite} value={periodo.fim}
            onChange={(e) => e.target.value && onChange({ ...periodo, fim: e.target.value })} className={`${CAMPO_MES} w-0 min-w-0 flex-1 sm:w-auto sm:flex-none`} />
        </span>
      )}
    </div>
  );
};

// ─────────────── gráfico ↔ resumo ───────────────

type Visao = 'grafico' | 'resumo';

const OPCOES_VISAO = [
  { id: 'grafico' as const, rotulo: 'Gráfico', Icone: IconeGrafico },
  { id: 'resumo' as const, rotulo: 'Resumo', Icone: Rows3 },
];

// ─────────────── bloco ───────────────

const DinheiroDoPeriodo: React.FC<{ agora: Date }> = ({ agora }) => {
  const { dados, loading, erro } = useFinanceiroDashboardData();
  const escuro = useModoEscuro();
  const cores = escuro ? CORES.escuro : CORES.claro;
  const [periodo, setPeriodo] = useState<PeriodoDashboard>({ tipo: 'preset', preset: 'mesAtual' });
  const [visaoEscolhida, setVisaoEscolhida] = useState<Visao | null>(null);

  const recorte = useMemo(() => {
    const janela = calcularJanelaPeriodoDashboard(periodo, agora);
    if (!janela) return { inicio: null, fim: null, limite: null, rotulo: 'todo o histórico', umMes: false };
    const fimDoMes = new Date(janela.fim.getFullYear(), janela.fim.getMonth() + 1, 0);
    const ini = yyyyMmParaMesReferencia(yyyyMm(janela.inicio)) ?? '';
    const fim = yyyyMmParaMesReferencia(yyyyMm(janela.fim)) ?? '';
    return {
      inicio: isoLocal(janela.inicio),
      fim: isoLocal(fimDoMes),
      limite: ultimoDiaUtilDoMes(isoLocal(fimDoMes)),
      rotulo: ini === fim ? ini : `${ini} – ${fim}`,
      umMes: ini === fim,
    };
  }, [periodo, agora]);

  // Um mês abre no resumo (a conta do mês); vários meses abrem no gráfico, que é a
  // pergunta ("como a inadimplência andou?"). O botão troca nos dois casos: o
  // gráfico de um mês mostra o mês dia a dia.
  const visao: Visao = visaoEscolhida ?? (recorte.umMes ? 'resumo' : 'grafico');
  const trocarPeriodo = (p: PeriodoDashboard) => { setPeriodo(p); setVisaoEscolhida(null); };

  const dinheiro = useMemo(
    () => calcularDinheiroDoPeriodo(dados, recorte.inicio, recorte.fim, agora, recorte.limite),
    [dados, recorte, agora],
  );
  const serie = useMemo(
    () => (visao === 'grafico' && !recorte.umMes ? serieMensalDinheiro(dados, recorte.inicio, recorte.fim, agora) : []),
    [dados, recorte, agora, visao],
  );
  const dias = useMemo(
    () => (visao === 'grafico' && recorte.umMes && recorte.inicio && recorte.fim
      ? serieDiariaDinheiro(dados, recorte.inicio, recorte.fim, agora, dinheiro.taxaRecebimento, recorte.limite)
      : []),
    [dados, recorte, agora, visao, dinheiro.taxaRecebimento],
  );
  const carregando = loading && dados.length === 0;

  // O título já é a resposta: quanto entrou e, se o período não acabou, onde fecha.
  const titulo = carregando || erro || dinheiro.esperado === 0
    ? 'Dinheiro'
    : dinheiro.previsao != null
      ? `${brlCurto(dinheiro.recebido)} recebidos de ${brlCurto(dinheiro.esperado)} · previsão de fechar com ${brlCurto(dinheiro.previsao)}`
      : `${brlCurto(dinheiro.recebido)} recebidos de ${brlCurto(dinheiro.esperado)} · ${pct(dinheiro.inadimplencia, 1)} não pagos`;

  return (
    <Bloco
      titulo={titulo}
      subtitulo={`Boletos que vencem em ${recorte.rotulo}`}
      acoes={
        <>
          <SeletorPeriodo periodo={periodo} agora={agora} onChange={trocarPeriodo} />
          <Alternador<Visao> rotulo="Forma de ver" opcoes={OPCOES_VISAO} valor={visao} onChange={setVisaoEscolhida} />
        </>
      }
    >
      {erro ? (
        <ErroBloco mensagem={erro} />
      ) : carregando ? (
        <LinhasCarregando linhas={2} rotulo="Somando os boletos…" />
      ) : dinheiro.esperado === 0 ? (
        <p className="px-5 pb-6 text-sm text-gray-500 dark:text-gray-400">
          Nenhum boleto vence em {recorte.rotulo}. Escolha outro período acima.
        </p>
      ) : (
        // `key` remonta o conteúdo na troca: o Surgir faz a passagem e o gráfico redesenha do zero.
        <Surgir key={`${visao}-${recorte.rotulo}`}>
          {visao === 'grafico' ? (
            recorte.umMes
              ? <EvolucaoDiaria dias={dias} d={dinheiro} cores={cores} agora={agora} />
              : <Evolucao serie={serie} total={dinheiro} cores={cores} />
          ) : (
            <Resumo d={dinheiro} umMes={recorte.umMes} cores={cores} />
          )}
        </Surgir>
      )}
    </Bloco>
  );
};

// ─────────────── resumo ───────────────

/**
 * A conta do período, linha a linha, como um extrato: o que vence, e em que pé
 * está cada parte (paga, atrasada, ainda vai vencer). As partes SOMAM o total —
 * é isso que a versão de três números soltos não deixava ver. A previsão vem
 * embaixo, dizendo de onde sai.
 */
const Resumo: React.FC<{ d: Dinheiro; umMes: boolean; cores: Cores }> = ({ d, umMes, cores }) => {
  const montado = useMontado();
  const parte = (v: number) => (d.esperado > 0 ? v / d.esperado : 0);
  const temPrevisao = d.previsao != null && !!d.limitePrevisao;
  const limite = d.limitePrevisao ? (umMes ? ddmm(d.limitePrevisao) : `${ddmm(d.limitePrevisao)}/${d.limitePrevisao.slice(0, 4)}`) : '';
  const quando = umMes ? 'no mês' : 'no período';
  const venceu = d.recebido + d.vencido;
  const doQueVence = d.previsao != null ? d.previsao - d.recebido : 0;

  return (
    <div className="px-5 pb-5">
      <div className="grid gap-x-10 gap-y-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        {/* O extrato */}
        <div>
          <Linha rotulo={`Vencem ${quando}`} valor={d.esperado} detalhe={`${d.boletos.toLocaleString('pt-BR')} boletos`} forte />
          <div className="mt-1 border-l-2 border-gray-100 pl-3 dark:border-gray-700">
            <Linha amostra={{ background: cores.recebido }} rotulo="Já pagos" valor={d.recebido} detalhe={pct(parte(d.recebido))} />
            <Linha amostra={{ background: cores.atrasado }} rotulo="Venceram e não foram pagos" valor={d.vencido} detalhe={pct(parte(d.vencido))} />
            {d.aVencer > 0 && (
              <Linha amostra={{ backgroundImage: TEXTURA_CSS(cores) }} rotulo={`Ainda vão vencer${limite ? ` até ${umMes ? ddmm(d.limitePrevisao!.slice(0, 8) + String(new Date(Number(d.limitePrevisao!.slice(0, 4)), Number(d.limitePrevisao!.slice(5, 7)), 0).getDate())) : 'o fim do período'}` : ''}`} valor={d.aVencer} detalhe={pct(parte(d.aVencer))} />
            )}
          </div>

          {/* A barra é o mesmo extrato, desenhado. O traço preto é a previsão. */}
          <div className="relative mt-4">
            <div
              role="img"
              aria-label={`De ${brl(d.esperado)}: ${brl(d.recebido)} pagos, ${brl(d.vencido)} atrasados, ${brl(d.aVencer)} a vencer`}
              className="flex h-3 gap-[2px] overflow-hidden rounded-full motion-reduce:!transition-none"
              style={{ clipPath: `inset(0 ${montado ? 0 : 100}% 0 0 round 9999px)`, transition: `clip-path 800ms ${CURVA_SAIDA}` }}
            >
              {d.recebido > 0 && <span className="h-full" style={{ width: `${parte(d.recebido) * 100}%`, background: cores.recebido }} />}
              {d.vencido > 0 && <span className="h-full" style={{ width: `${parte(d.vencido) * 100}%`, background: cores.atrasado }} />}
              {d.aVencer > 0 && <span className="h-full flex-1" style={{ backgroundImage: TEXTURA_CSS(cores) }} />}
            </div>
            {temPrevisao && (
              <span
                className="absolute -bottom-1.5 -top-1.5 w-0.5 rounded-full bg-gray-900 motion-reduce:!transition-none dark:bg-white"
                style={{ left: `${Math.min(parte(d.previsao!), 1) * 100}%`, opacity: montado ? 1 : 0, transition: 'opacity 200ms ease-out 600ms' }}
                title={`Previsão: ${brl(d.previsao!)}`}
              />
            )}
          </div>
        </div>

        {/* As duas respostas: onde fecha e quanto não pagaram */}
        <div className="grid content-start gap-4 sm:grid-cols-2 lg:grid-cols-1">
          {temPrevisao && (
            <div className="rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-900/40">
              <p className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                <span className="h-3 w-0.5 rounded-full bg-gray-900 dark:bg-white" aria-hidden /> Previsão até {limite}
              </p>
              <p className="mt-1 font-montserrat text-[28px] font-bold leading-none tracking-tight tabular-nums text-gray-900 dark:text-white" title={brl(d.previsao!)}>
                {brlCurto(d.previsao!)} <span className="text-base font-semibold text-gray-400">{pct(parte(d.previsao!))}</span>
              </p>
              <p className="mt-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                Os {brlCurto(d.recebido)} já pagos + {brlCurto(doQueVence)} dos que ainda vão vencer
                ({pct(d.taxaRecebimento)} costuma ser pago). Os {brlCurto(d.vencido)} atrasados ficam de fora.
              </p>
            </div>
          )}
          <div className="rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-900/40">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Inadimplência {umMes && d.aVencer > 0 ? 'até agora' : quando}</p>
            <p className={`mt-1 font-montserrat text-[28px] font-bold leading-none tracking-tight tabular-nums ${d.inadimplencia != null && d.inadimplencia >= 0.1 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-900 dark:text-white'}`}>
              {d.inadimplencia == null ? '—' : pct(d.inadimplencia, 1)}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
              {d.inadimplencia == null
                ? 'Nenhum boleto do período venceu ainda.'
                : `Dos ${brlCurto(venceu)} que já venceram, ${brlCurto(d.vencido)} não foram pagos.`}
              {umMes && d.aVencer > 0 && d.inadimplencia != null ? ' Parte disso é atraso de poucos dias, que ainda costuma entrar.' : ''}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Uma linha do extrato: amostra de cor, o que é, quanto e a parte do total. */
const Linha: React.FC<{ rotulo: string; valor: number; detalhe: string; amostra?: React.CSSProperties; forte?: boolean }> = ({ rotulo, valor, detalhe, amostra, forte }) => (
  <div className={`flex items-baseline gap-3 py-1.5 ${forte ? '' : 'text-sm'}`}>
    {amostra && <span className="h-2.5 w-2.5 shrink-0 translate-y-[-1px] rounded-[3px]" style={amostra} aria-hidden />}
    <span className={`min-w-0 flex-1 ${forte ? 'text-sm font-semibold text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`}>{rotulo}</span>
    <span className={`tabular-nums ${forte ? 'font-montserrat text-xl font-bold text-gray-900 dark:text-white' : 'font-semibold text-gray-900 dark:text-white'}`} title={brl(valor)}>{brlCurto(valor)}</span>
    <span className="w-24 shrink-0 text-right text-xs tabular-nums text-gray-400">{detalhe}</span>
  </div>
);

const Legenda: React.FC<{ amostra: React.CSSProperties; rotulo: string; valor: number }> = ({ amostra, rotulo, valor }) => (
  <span className="inline-flex items-center gap-1.5" title={brl(valor)}>
    <span className="h-2.5 w-2.5 rounded-[3px]" style={amostra} aria-hidden />
    {rotulo} <strong className="font-semibold tabular-nums text-gray-800 dark:text-gray-200">{brlCurto(valor)}</strong>
  </span>
);

// ─────────────── evolução ───────────────

const MES_CURTO: Record<string, string> = {
  JAN: 'jan', FEV: 'fev', MAR: 'mar', ABR: 'abr', MAI: 'mai', JUN: 'jun', JUL: 'jul', AGO: 'ago', SET: 'set', OUT: 'out', NOV: 'nov', DEZ: 'dez',
};
const rotuloCurto = (mes: string) => `${MES_CURTO[mes.slice(0, 3)] ?? mes.slice(0, 3)}/${mes.slice(6)}`;

interface PontoGrafico {
  mes: string;
  rotulo: string;
  inad: number | null;
  recebido: number;
  atrasado: number;
  aVencer: number;
  cobrado: number;
  emAndamento: boolean;
}

function eixoReais(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)} mil`;
  return String(v);
}

const Evolucao: React.FC<{ serie: MesDinheiro[]; total: Dinheiro; cores: Cores }> = ({ serie, total, cores }) => {
  const pontos: PontoGrafico[] = serie.map((m) => ({
    mes: m.mes,
    rotulo: rotuloCurto(m.mes),
    inad: m.inadimplencia == null ? null : m.inadimplencia * 100,
    recebido: m.recebido,
    atrasado: m.vencido,
    aVencer: m.aVencer,
    cobrado: m.esperado,
    emAndamento: m.emAndamento,
  }));
  const comInad = pontos.filter((p) => p.inad != null);
  const pior = comInad.reduce<PontoGrafico | null>((a, p) => (!a || (p.inad ?? 0) > (a.inad ?? 0) ? p : a), null);
  const ultimo = comInad[comInad.length - 1] ?? null;
  const media = total.inadimplencia == null ? null : total.inadimplencia * 100;
  // Eixo em degraus redondos (5 em 5 até 20%, depois 10 em 10): o automático
  // do Recharts dividia 0–35 em 9%, 18%, 27%.
  const passoInad = (pior?.inad ?? 0) * 1.25 > 20 ? 10 : 5;
  const tetoInad = Math.max(10, Math.ceil(((pior?.inad ?? 0) * 1.25) / passoInad) * passoInad);
  const ticksInad = Array.from({ length: tetoInad / passoInad + 1 }, (_, i) => i * passoInad);

  // Só dois números escritos no gráfico: o último mês (onde estamos) e o pior.
  const rotularPonto = (props: any) => {
    const p = pontos[props.index];
    if (!p || p.inad == null || (p !== ultimo && p !== pior)) return null;
    return (
      <text x={props.x} y={props.y - 10} textAnchor="middle" fontSize={11} fontWeight={600} className="fill-gray-800 dark:fill-gray-100">
        {pctTexto(p.inad)}
      </text>
    );
  };
  const desenharPonto = (props: any) => {
    const p = pontos[props.index];
    if (!p || p.inad == null || props.cx == null) return <g key={props.key ?? props.index} />;
    return (
      <circle key={props.key ?? props.index} cx={props.cx} cy={props.cy} r={4}
        fill={p.emAndamento ? cores.superficie : cores.atrasado} stroke={cores.atrasado} strokeWidth={2} />
    );
  };

  return (
    <div className="px-5 pb-5">
      {/* O período inteiro numa linha: a manchete antes do desenho. */}
      <dl className="mb-5 grid grid-cols-1 gap-x-8 gap-y-3 rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-900/40 sm:grid-cols-3">
        <Resumido rotulo="Recebido no período" valor={brlCurto(total.recebido)} detalhe={`de ${brlCurto(total.esperado)} cobrados (${pct(total.recebido / total.esperado)})`} />
        <Resumido rotulo="Inadimplência do período" valor={media == null ? '—' : pctTexto(media)} detalhe={`${brlCurto(total.vencido)} vencidos sem pagar`} />
        <Resumido
          rotulo="Pior mês"
          valor={pior ? `${rotuloCurto(pior.mes)} · ${pctTexto(pior.inad!)}` : '—'}
          detalhe={pior ? `${brlCurto(pior.atrasado)} atrasados de ${brlCurto(pior.cobrado)}` : ''}
        />
      </dl>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <figure className="lg:col-span-3">
          <figcaption className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Inadimplência mês a mês</figcaption>
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={pontos} margin={{ top: 22, right: 12, bottom: 0, left: -12 }} syncId="ceo-dinheiro">
                <defs>
                  <linearGradient id="ceoInadArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={cores.atrasado} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={cores.atrasado} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke={cores.grade} />
                <XAxis dataKey="rotulo" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: cores.eixo }} interval="preserveStartEnd" minTickGap={8} />
                <YAxis domain={[0, tetoInad]} ticks={ticksInad} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: cores.eixo }} tickFormatter={(v) => `${v}%`} width={44} />
                {media != null && (
                  <ReferenceLine
                    y={media}
                    stroke={cores.eixo}
                    strokeDasharray="4 4"
                    label={{ value: `média ${pctTexto(media)}`, position: 'insideBottomRight', fontSize: 10, fill: cores.eixo }}
                  />
                )}
                <Tooltip content={<DicaMes cores={cores} />} cursor={{ stroke: cores.eixo, strokeDasharray: '3 3' }} />
                <Area
                  type="monotone"
                  dataKey="inad"
                  stroke={cores.atrasado}
                  strokeWidth={2}
                  fill="url(#ceoInadArea)"
                  connectNulls
                  dot={desenharPonto}
                  activeDot={{ r: 6, stroke: cores.superficie, strokeWidth: 2, fill: cores.atrasado }}
                  isAnimationActive
                  animationDuration={600}
                  animationEasing="ease-out"
                >
                  <LabelList dataKey="inad" content={rotularPonto} />
                </Area>
              </AreaChart>
            </ResponsiveContainer>
          </div>
          {pontos.some((p) => p.emAndamento) && (
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              Ponto vazado: mês com boleto ainda a vencer — a inadimplência dele ainda vai mudar.
            </p>
          )}
        </figure>

        <figure className="lg:col-span-2">
          <figcaption className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Cobrado x recebido</figcaption>
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pontos} margin={{ top: 22, right: 4, bottom: 0, left: -6 }} syncId="ceo-dinheiro" barCategoryGap="22%">
                <defs>
                  <pattern id="ceoAVencer" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                    <rect width="6" height="6" fill={cores.fundo} />
                    <line x1="0" y1="0" x2="0" y2="6" stroke={cores.textura} strokeWidth="3" />
                  </pattern>
                </defs>
                <CartesianGrid vertical={false} stroke={cores.grade} />
                <XAxis dataKey="rotulo" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: cores.eixo }} interval="preserveStartEnd" minTickGap={8} />
                <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: cores.eixo }} tickFormatter={eixoReais} width={60} />
                <Tooltip content={<DicaMes cores={cores} />} cursor={{ fill: cores.grade, opacity: 0.5 }} />
                {/* Contorno da cor do fundo: 2px de respiro entre os pedaços empilhados. */}
                <Bar dataKey="recebido" stackId="m" fill={cores.recebido} stroke={cores.superficie} strokeWidth={2} isAnimationActive animationDuration={600} animationEasing="ease-out" />
                <Bar dataKey="atrasado" stackId="m" fill={cores.atrasado} stroke={cores.superficie} strokeWidth={2} isAnimationActive animationDuration={600} animationEasing="ease-out" />
                <Bar dataKey="aVencer" stackId="m" fill="url(#ceoAVencer)" stroke={cores.superficie} strokeWidth={2} radius={[4, 4, 0, 0]} isAnimationActive animationDuration={600} animationEasing="ease-out" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
            <LegendaPequena amostra={{ background: cores.recebido }} rotulo="Recebido" />
            <LegendaPequena amostra={{ background: cores.atrasado }} rotulo="Atrasado" />
            <LegendaPequena amostra={{ backgroundImage: TEXTURA_CSS(cores) }} rotulo="Ainda vai vencer" />
          </p>
        </figure>
      </div>
    </div>
  );
};

/**
 * Um mês, dia a dia, acumulado e num eixo só (R$): a área cinza é o que VENCEU
 * até cada dia; a linha verde, o que desses foi PAGO; a tracejada, a PREVISÃO de
 * hoje até o fim do mês. A distância entre a área e a linha é o atraso.
 */
const EvolucaoDiaria: React.FC<{ dias: DiaDinheiro[]; d: Dinheiro; cores: Cores; agora: Date }> = ({ dias, d, cores, agora }) => {
  const hojeIso = isoLocal(agora);
  const pontos = dias.map((x) => ({
    rotulo: String(Number(x.dia.slice(8, 10))),
    dia: x.dia,
    venceu: x.venceuAcum,
    pago: x.dia <= hojeIso ? x.pagoAcum : null,
    previsao: x.previsaoAcum,
  }));
  const rotuloHoje = String(agora.getDate());
  const temHoje = pontos.some((p) => p.dia === hojeIso);
  return (
    <div className="px-5 pb-5">
      <figure>
        <figcaption className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">O mês dia a dia</span>
          <span className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
            <LegendaPequena amostra={{ background: cores.grade, border: `1px solid ${cores.eixo}` }} rotulo="Venceu (acumulado)" />
            <LegendaPequena amostra={{ background: cores.recebido }} rotulo="Pago" />
            <LegendaPequena amostra={{ height: 0, width: 14, borderRadius: 0, borderTop: `2px dashed ${cores.recebido}` }} rotulo="Previsão" />
          </span>
        </figcaption>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={pontos} margin={{ top: 12, right: 12, bottom: 0, left: -6 }}>
              <CartesianGrid vertical={false} stroke={cores.grade} />
              <XAxis dataKey="rotulo" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: cores.eixo }} interval="preserveStartEnd" minTickGap={10} />
              <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: cores.eixo }} tickFormatter={eixoReais} width={60} />
              {temHoje && (
                <ReferenceLine x={rotuloHoje} stroke={cores.eixo} strokeDasharray="3 3"
                  label={{ value: 'hoje', position: 'insideTopRight', fontSize: 10, fill: cores.eixo }} />
              )}
              <Tooltip content={<DicaDia cores={cores} />} cursor={{ stroke: cores.eixo, strokeDasharray: '3 3' }} />
              <Area type="stepAfter" dataKey="venceu" stroke={cores.eixo} strokeWidth={1} fill={cores.grade} fillOpacity={0.7}
                isAnimationActive animationDuration={600} animationEasing="ease-out" />
              <Line type="monotone" dataKey="pago" stroke={cores.recebido} strokeWidth={2.5} dot={false} connectNulls={false}
                isAnimationActive animationDuration={600} animationEasing="ease-out" />
              <Line type="monotone" dataKey="previsao" stroke={cores.recebido} strokeWidth={2} strokeDasharray="5 4" dot={false}
                isAnimationActive animationDuration={600} animationEasing="ease-out" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
          Até hoje venceram {brlCurto(d.recebido + d.vencido)} e foram pagos {brlCurto(d.recebido)}
          {d.vencido > 0 ? ` — a distância entre a área e a linha verde são os ${brlCurto(d.vencido)} atrasados` : ''}.
          {d.previsao != null ? ` A tracejada mostra onde o mês deve fechar: ${brlCurto(d.previsao)} de ${brlCurto(d.esperado)}.` : ''}
        </p>
      </figure>
    </div>
  );
};

const DicaDia: React.FC<any> = ({ active, payload, cores }) => {
  const p = payload?.[0]?.payload;
  if (!active || !p) return null;
  const linhas = [
    { rotulo: 'Venceu até o dia', valor: p.venceu, amostra: { background: cores.grade, border: `1px solid ${cores.eixo}` } },
    ...(p.pago != null ? [{ rotulo: 'Pago', valor: p.pago, amostra: { background: cores.recebido } }] : []),
    ...(p.previsao != null ? [{ rotulo: 'Previsão', valor: p.previsao, amostra: { background: cores.recebido, opacity: 0.6 } }] : []),
  ];
  return (
    <div className="min-w-[180px] rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-xs shadow-lg dark:border-gray-600 dark:bg-gray-800">
      <p className="mb-1.5 font-semibold text-gray-900 dark:text-white">Dia {Number(String(p.dia).slice(8, 10))}/{String(p.dia).slice(5, 7)}</p>
      {linhas.map((l) => (
        <p key={l.rotulo} className="flex items-center justify-between gap-4 py-0.5 text-gray-600 dark:text-gray-300">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-[2px]" style={l.amostra} />{l.rotulo}</span>
          <span className="font-semibold tabular-nums text-gray-900 dark:text-white">{brlCurto(l.valor)}</span>
        </p>
      ))}
      {p.pago != null && p.venceu - p.pago > 1 && (
        <p className="mt-1.5 border-t border-gray-100 pt-1.5 text-gray-500 dark:border-gray-700 dark:text-gray-400">
          Atrasado até aqui: <span className="font-semibold text-orange-600 dark:text-orange-400">{brlCurto(p.venceu - p.pago)}</span>
        </p>
      )}
    </div>
  );
};

const Resumido: React.FC<{ rotulo: string; valor: string; detalhe: string }> = ({ rotulo, valor, detalhe }) => (
  <div className="min-w-0">
    <dt className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{rotulo}</dt>
    <dd className="mt-0.5 font-montserrat text-lg font-bold tabular-nums text-gray-900 dark:text-white">{valor}</dd>
    <dd className="text-[11px] text-gray-500 dark:text-gray-400">{detalhe}</dd>
  </div>
);

const LegendaPequena: React.FC<{ amostra: React.CSSProperties; rotulo: string }> = ({ amostra, rotulo }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="h-2.5 w-2.5 rounded-[3px]" style={amostra} aria-hidden /> {rotulo}
  </span>
);

/** Dica do mês sob o cursor — a mesma nos dois gráficos (eles andam juntos pelo `syncId`). */
const DicaMes: React.FC<any> = ({ active, payload, cores }) => {
  const p: PontoGrafico | undefined = payload?.[0]?.payload;
  if (!active || !p) return null;
  const linhas: { rotulo: string; valor: number; amostra: React.CSSProperties }[] = [
    { rotulo: 'Recebido', valor: p.recebido, amostra: { background: cores.recebido } },
    { rotulo: 'Atrasado', valor: p.atrasado, amostra: { background: cores.atrasado } },
    ...(p.aVencer > 0 ? [{ rotulo: 'Ainda vai vencer', valor: p.aVencer, amostra: { backgroundImage: TEXTURA_CSS(cores) } }] : []),
  ];
  return (
    <div className="min-w-[190px] rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-xs shadow-lg dark:border-gray-600 dark:bg-gray-800">
      <p className="mb-1.5 flex items-baseline justify-between gap-4 font-semibold text-gray-900 dark:text-white">
        {p.mes}
        <span className="tabular-nums text-orange-600 dark:text-orange-400">{p.inad == null ? '—' : `${pctTexto(p.inad)} inadimpl.`}</span>
      </p>
      {linhas.map((l) => (
        <p key={l.rotulo} className="flex items-center justify-between gap-4 py-0.5 text-gray-600 dark:text-gray-300">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-[2px]" style={l.amostra} />{l.rotulo}</span>
          <span className="font-semibold tabular-nums text-gray-900 dark:text-white">{brlCurto(l.valor)}</span>
        </p>
      ))}
      <p className="mt-1.5 flex justify-between gap-4 border-t border-gray-100 pt-1.5 text-gray-500 dark:border-gray-700 dark:text-gray-400">
        Cobrado <span className="font-semibold tabular-nums text-gray-800 dark:text-gray-200">{brlCurto(p.cobrado)}</span>
      </p>
      {p.emAndamento && <p className="mt-1 text-[10px] text-gray-400">Ainda tem boleto a vencer neste mês.</p>}
    </div>
  );
};

export default DinheiroDoPeriodo;
